import { useNavigate, useParams } from "react-router-dom";
import { Button as UIButton } from "../../../components/ui";
import { Select, Option } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import apiService from "../../../services/apiService";
import { useToast } from "../../../context/ToastContext";
import { FaArrowLeft } from "react-icons/fa";
import Loader from "../../../components/ui/Loader";

const ViewTask = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const taskQuestionId = useParams().id;
  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState(null);
  const [status, setStatus] = useState("");
  const getId = (value) => {
    if (!value) return "";
    if (typeof value === "object") return value.id || "";
    return value;
  };

  const normalizeArrayResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const result = await apiService.request_old(
          `/i/ims/taskquestion/${taskQuestionId}?populate=subject,course,stream,curriculum,template`,
          { method: "GET" },
        );

        const record = result?.data || result;
        const taskData = record?.taskId || record;

        if (!taskData) {
          showError("Task data not found");
          return;
        }
        const subjectId = getId(taskData.subject);
        let allUnits = [];
        if (subjectId) {
          const allUnitsResponse = await apiService.request_old(
            `/i/ims/subjectunit?subject=${subjectId}&status=true`,
            { method: "GET" },
          );
          allUnits = normalizeArrayResponse(allUnitsResponse);
        }

        const unitsWithDetails = await Promise.all(
          (taskData.units || []).map(async (unit) => {
            const unitObject =
              allUnits.find((singleUnit) => singleUnit.id === unit.unitId) ||
              null;
            let allTopics = [];
            try {
              const allTopicsResponse = await apiService.request_old(
                `/i/ims/topic?unit=${unit.unitId}&status=true`,
                { method: "GET" },
              );

              allTopics = normalizeArrayResponse(allTopicsResponse);
            } catch (err) {
              console.log("Topic fetch error:", err);
            }

            const topicsWithDetails = (unit.topics || []).map((topic) => {
              const topicObject =
                allTopics.find(
                  (singleTopic) => singleTopic.id === topic.topicId,
                ) || null;

              return {
                ...topic,
                topicObject,
              };
            });

            return {
              ...unit,
              unitObject,
              topics: topicsWithDetails,
            };
          }),
        );
        const finalTask = {
          ...taskData,
          taskQuestionId: record?.id,
          questionImages: record?.questionImages || [],
          taskQuestion: record,
          units: unitsWithDetails,
        };
        console.log("finalTask", finalTask);
        setTask(finalTask);
        setStatus(finalTask?.status || "");
      } catch (err) {
        console.log(err);
        showError("Failed to fetch task");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [taskQuestionId]);

  async function updateTask(newStatus) {
    const oldStatus = status;
    setStatus(newStatus);
    try {
      const response = await apiService.request_old(
        `/i/ims/taskquestion/${taskQuestionId}`,
        {
          method: "PUT",
          body: JSON.stringify({ status: newStatus }),
        },
      );
      console.log("response", response);
      setTask((prev) => ({
        ...prev,
        status: newStatus,
      }));

      showSuccess("Status Updated");
    } catch (err) {
      console.log(err);
      setStatus(oldStatus);
      showError("Status Update Failed");
    }
  }
  if (loading) {
    return (
      <div className="p-5">
        <Loader />
      </div>
    );
  }
  return (
    <div className="p-2 relative">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">View Task</h1>

        <UIButton
          onClick={() => navigate("/tools/task")}
          variant="light"
          size="sm"
          icon={FaArrowLeft}
        >
          Back to List
        </UIButton>
      </div>

      <div className="flex items-center text-sm mb-6">
        <span className="text-gray-500">Home</span>
        <span className="mx-2">/</span>

        <span
          onClick={() => navigate("/tools/task")}
          className="text-gray-500 cursor-pointer"
        >
          Task
        </span>

        <span className="mx-2">/</span>
        <span className="text-blue-600">View</span>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="p-2 ml-5 mr-5">
          <div className="flex flex-col justify-around sm:flex-row sm:justify-around">
            <div className="p-3 flex gap-2">
              <label className="font-semibold text-gray-700">Title :</label>
              <label className="text-gray-800">{task?.title || "-"}</label>
            </div>

            <div className="p-3 flex min-w-[180px]">
              <Select
                label="Status"
                value={status || ""}
                onChange={(val) => updateTask(val)}
              >
                {(task?.template?.value?.status || ["Pending", "Done"]).map(
                  (item) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ),
                )}
              </Select>
            </div>

            <div className="p-3 items-center">
              <UIButton
                onClick={() =>
                  navigate(`/tools/task/view/${taskQuestionId}/questions`)
                }
                variant="primary"
                size="sm"
              >
                Upload Questions
              </UIButton>
            </div>
          </div>

          <div className="mt-3">
            <table className="w-full border border-gray-300 rounded-lg shadow-md">
              <tbody>
                <tr className="border-b last:border-none">
                  <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Subject
                  </th>
                  <td className="py-3 px-4 text-gray-800">
                    {task?.subject?.name || task?.subject?.value || "-"}
                  </td>
                </tr>

                <tr className="border-b last:border-none">
                  <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Course
                  </th>
                  <td className="py-3 px-4 text-gray-800">
                    {task?.course?.value || task?.course?.title || "-"}
                  </td>
                </tr>

                <tr className="border-b last:border-none">
                  <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Stream
                  </th>
                  <td className="py-3 px-4 text-gray-800">
                    {task?.stream?.value || task?.stream?.title || "-"}
                  </td>
                </tr>

                <tr className="border-b last:border-none">
                  <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Curriculum
                  </th>
                  <td className="py-3 px-4 text-gray-800">
                    {task?.curriculum?.name ||
                      task?.curriculum?.title ||
                      task?.curriculum?.value ||
                      "-"}
                  </td>
                </tr>

                <tr className="border-b last:border-none">
                  <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Template
                  </th>
                  <td className="py-3 px-4 text-gray-800">
                    {task?.template?.value?.title ||
                      task?.template?.title ||
                      task?.template?.value ||
                      "-"}
                  </td>
                </tr>

                <tr className="border-b last:border-none">
                  <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Total Questions
                  </th>
                  <td className="py-3 px-4 text-gray-800">
                    {task?.totalQuestion || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-5 mb-5 overflow-x-auto">
            <table className="w-full border border-gray-300 rounded-lg shadow-md">
              <thead>
                <tr>
                  <td className="w-2/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Unit
                  </td>
                  <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Topics
                  </td>
                  <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Type
                  </td>
                  <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    Difficulty
                  </td>
                  <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                    No of Questions
                  </td>
                </tr>
              </thead>

              <tbody>
                {(task?.units || []).length > 0 ? (
                  task.units.map((unit, i) =>
                    (unit?.topics || []).map((topic, j) => (
                      <tr key={`${i}-${j}`} className="border-b">
                        <td className="w-2/6 py-3 px-4 text-gray-800">
                          {unit?.unitObject?.name || "-"}
                        </td>

                        <td className="w-1/6 py-3 px-4 text-gray-800">
                          {topic?.topicObject?.name || "-"}
                        </td>

                        <td className="w-1/6 py-3 px-4 text-gray-800">
                          {topic?.type || "-"}
                        </td>

                        <td className="w-1/6 py-3 px-4 text-gray-800">
                          {topic?.difficulty || "-"}
                        </td>

                        <td className="w-1/6 py-3 px-4 text-gray-800">
                          {topic?.questions || 0}
                        </td>
                      </tr>
                    )),
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-4 px-4 text-center text-gray-500"
                    >
                      No units found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* {task?.questionImages?.length > 0 && (
            <div className="mt-5 mb-5 overflow-x-auto">
              <h2 className="text-lg font-semibold mb-3">Question Images</h2>

              <table className="w-full border border-gray-300 rounded-lg shadow-md">
                <thead>
                  <tr>
                    <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                      Question No.
                    </td>
                    <td className="w-5/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
                      Images
                    </td>
                  </tr>
                </thead>

                <tbody>
                  {task.questionImages.map((question, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-3 px-4 text-gray-800">
                        {question?.questionNum}
                      </td>

                      <td className="py-3 px-4 text-gray-800">
                        {question?.imagesArr?.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {question.imagesArr.map((img, imgIndex) => (
                              <a
                                key={imgIndex}
                                href={img}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 underline text-sm"
                              >
                                Image {imgIndex + 1}
                              </a>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default ViewTask;

// import { useNavigate, useParams } from "react-router-dom";
// import { Button as UIButton } from "../../../components/ui";
// import { Controller, useForm } from "react-hook-form";
// import {
//   Select,
//   Option,
//   Accordion,
//   AccordionHeader,
//   AccordionBody,
// } from "@material-tailwind/react";
// import { useEffect, useState } from "react";
// import apiService from "../../../services/apiService";
// import { useToast } from "../../../context/ToastContext";
// import { FaArrowLeft } from "react-icons/fa";
// import Loader from "../../../components/ui/Loader";
// import axios from "axios";

// function Icon({ id, open }) {
//   return (
//     <svg
//       xmlns="http://www.w3.org/2000/svg"
//       fill="none"
//       viewBox="0 0 24 24"
//       strokeWidth={2}
//       stroke="currentColor"
//       className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
//     >
//       <path
//         strokeLinecap="round"
//         strokeLinejoin="round"
//         d="M19.5 8.25l-7.5 7.5-7.5-7.5"
//       />
//     </svg>
//   );
// }

// const ViewTask = () => {
//   const handleOpen = (value) => setOpen(open === value ? 0 : value);
//   const { showSuccess, showError, showInfo, showWarning } = useToast();
//   const navigate = useNavigate();
//   const taskId = useParams().id;
//   const [loading, setLoading] = useState(false);
//   const [task, setTask] = useState();
//   const [status, setStatus] = useState();
//   const [open, setOpen] = useState(0);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         let result = await apiService.request_old(
//           `/i/ims/taskquestion/${taskId}?populate=subject,course,stream,curriculum,template`,
//           { method: "GET" },
//         );
//         const allUnits = await apiService.abc_request(
//           `/admin/subjectunit?subject=${result.subject.id}&status=true`,
//           { method: "GET" },
//         );
//         await Promise.all(
//           result["units"].map(async (unit) => {
//             unit.unitObject = allUnits.filter(
//               (singleUnit) => singleUnit.id === unit.unitId,
//             )[0];
//             const allTopics = await axios.get(
//               `https://r1.edkt.net/api/i/ims/topic?unit=${unit.unitId}&status=true`,
//               {
//                 headers: {
//                   //Token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjp7ImlkIjoiNWU5MDc2ZmYzYTE4ZTQ2OGM5MWE1NjFjIiwiZmlyc3ROYW1lIjoiQWJjIiwibGFzdE5hbWUiOiJTY2hvb2wiLCJlbWFpbCI6ImFiY3NjaG9vbEBnbWFpbC5jb20iLCJtb2JpbGUiOiI5NDE0NzQ3NjA3IiwidXNlcm5hbWUiOiJhYmNzY2hvb2xAZ21haWwuY29tIiwidHlwZSI6IkFETUlOIiwiY2xpZW50IjoiNWU5MDc2ZmU4MDdjOGIwNWE4M2U4YjhmIiwiZGVmYXVsdFBhcmFtcyI6e319LCJpYXQiOjE3NTc5MTU4OTgsImF1ZCI6ImV6dWtpdC5jb20iLCJpc3MiOiJFenVraXQifQ.2QdgGHJCQFRitVgpFP8YcF2FbzYP-mE2aiA2Sqngs44'
//                   Token:
//                     "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjp7ImlkIjoiNWU5MDc2ZmYzYTE4ZTQ2OGM5MWE1NjFjIiwiZmlyc3ROYW1lIjoiQWJjIiwibGFzdE5hbWUiOiJTY2hvb2wiLCJlbWFpbCI6ImZha2UxMzAyNkBnbWFpbC5jb20iLCJtb2JpbGUiOiI4Mzg0ODIzOTkwIiwidXNlcm5hbWUiOiJhYmNzY2hvb2xAZ21haWwuY29tIiwidHlwZSI6IkFETUlOIiwiY2xpZW50IjoiNWU5MDc2ZmU4MDdjOGIwNWE4M2U4YjhmIiwiZGVmYXVsdFBhcmFtcyI6e319LCJpYXQiOjE3ODMzNDAxNTcsImF1ZCI6ImV6dWtpdC5jb20iLCJpc3MiOiJFenVraXQifQ.LlTSSmw1NJd5QMx1CHPhJ26JO6cM4oyZLsVk5PcIKCY",
//                 },
//               },
//             );
//             unit.topics.forEach((topic) => {
//               topic.topicObject = allTopics["data"].filter(
//                 (singleTopic) => singleTopic.id === topic.topicId,
//               )[0];
//             });
//             return unit;
//           }),
//         );

//         console.log(result);
//         setTask(result);
//         setStatus(result.status);
//       } catch (err) {
//         console.log(err);
//       }
//     };

//     fetchData();
//   }, []);

//   async function updateTask(status) {
//     setStatus(status);
//     let newTask = { ...task };
//     const oldStatus = newTask.status;
//     newTask.status = status;

//     try {
//       const response = await apiService.request_old(
//         `/i/ims/taskquestion/${taskId}`,
//         { method: "PUT", body: JSON.stringify({ status: status }) },
//       );
//       console.log("response", response);
//       setTask(newTask);
//       setStatus(newTask.status);
//       showSuccess("Status Updated");
//     } catch (err) {
//       console.log(err);
//       console.log("oldStatus", oldStatus);
//       setStatus(oldStatus);
//       showError("Status Update Failed");
//     }
//   }

//   return (
//     <div className="p-2 relative">
//       <div className="flex justify-between items-center mb-6">
//         <h1 className="text-2xl font-bold">View Task</h1>
//         <UIButton
//           onClick={() => navigate("/tools/task")}
//           variant="light"
//           size="sm"
//           icon={FaArrowLeft}
//         >
//           Back to List
//         </UIButton>
//       </div>

//       <div className="flex items-center text-sm mb-6">
//         <span className="text-gray-500">Home</span>
//         <span className="mx-2">/</span>
//         <span onClick={() => navigate("/tools/qSet")} className="text-gray-500">
//           Task
//         </span>
//         <span className="mx-2">/</span>
//         <span className="text-blue-600">View</span>
//       </div>

//       <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
//         <div className="p-2 ml-5 mr-5">
//           <div className="flex flex-col justify-around sm:flex-row sm:justify-around">
//             <div className="p-3 flex">
//               <label className="font-semibold text-gray-700">Title : </label>
//               <label className="text-gray-800">{task?.title}</label>
//             </div>

//             <div className="p-3 flex">
//               {/* <label className='font-semibold text-gray-700'>Status : </label> */}
//               <Select
//                 label="Status"
//                 value={status}
//                 onChange={(val) => updateTask(val)}
//               >
//                 <Option value="Pending">Pending</Option>
//                 <Option value="Done">Done</Option>
//               </Select>
//               {/* <label className="text-gray-800">{task?.status}</label> */}
//             </div>

//             <div className="p-3 items-center">
//               <UIButton
//                 onClick={() => navigate(`/tools/task/view/${taskId}/questions`)}
//                 variant="primary"
//                 size="sm"
//                 // icon={FaArrowLeft}
//               >
//                 Upload Questions
//               </UIButton>
//             </div>
//           </div>

//           <div className="mt-3">
//             <table className="w-full border border-gray-300 rounded-lg shadow-md">
//               {" "}
//               {/*min-w-[300px]*/}
//               <tbody>
//                 <tr className="border-b last:border-none">
//                   <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Subject
//                   </th>
//                   <td className="py-3 px-4 text-gray-800">
//                     {task?.subject["name"]}
//                   </td>
//                 </tr>

//                 <tr className="border-b last:border-none">
//                   <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Course
//                   </th>
//                   <td className="py-3 px-4 text-gray-800">
//                     {task?.course["value"]}
//                   </td>
//                 </tr>

//                 <tr className="border-b last:border-none">
//                   <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Stream
//                   </th>
//                   <td className="py-3 px-4 text-gray-800">
//                     {task?.stream["value"]}
//                   </td>
//                 </tr>

//                 <tr className="border-b last:border-none">
//                   <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Curriculum
//                   </th>
//                   <td className="py-3 px-4 text-gray-800">
//                     {task?.curriculum["name"]}
//                   </td>
//                 </tr>

//                 <tr className="border-b last:border-none">
//                   <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Template
//                   </th>
//                   <td className="py-3 px-4 text-gray-800">
//                     {task?.template["title"]}
//                   </td>
//                 </tr>

//                 <tr className="border-b last:border-none">
//                   <th className="w-1/3 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Total Questions
//                   </th>
//                   <td className="py-3 px-4 text-gray-800">
//                     {task?.questionCount}
//                   </td>
//                 </tr>
//               </tbody>
//             </table>
//           </div>

//           <div className="mt-5 mb-5 overflow-x-auto ">
//             <table className="w-full border border-gray-300 rounded-lg shadow-md">
//               <thead>
//                 <tr>
//                   <td className="w-2/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Unit
//                   </td>
//                   <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Topics
//                   </td>
//                   <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Type
//                   </td>
//                   <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     Difficulty
//                   </td>
//                   <td className="w-1/6 py-3 px-4 text-left bg-gray-100 font-semibold text-gray-700">
//                     No of Questions
//                   </td>
//                 </tr>
//               </thead>

//               <tbody>
//                 {task?.units.map((unit, i) =>
//                   unit["topics"].map((topic, j) => (
//                     <tr key={`${i}-${j}`}>
//                       <td className="w-2/6 py-3 px-4 text-gray-800">
//                         {unit.unitObject?.name}
//                       </td>
//                       <td className="w-1/6 py-3 px-4 text-gray-800">
//                         {topic.topicObject?.name}
//                       </td>
//                       <td className="w-1/6 py-3 px-4 text-gray-800">
//                         {topic.type}
//                       </td>
//                       <td className="w-1/6 py-3 px-4 text-gray-800">
//                         {topic.difficulty}
//                       </td>
//                       <td className="w-1/6 py-3 px-4 text-gray-800">
//                         {topic.questions}
//                       </td>
//                     </tr>
//                   )),
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ViewTask;
